import { useState, useEffect } from 'react';

/**
 * Hook to fetch real stock data from backend API
 * Combines live prices with trajectory animation data
 * Supports timeframe selection and lazy loading
 */
export const useStockData = (timeframe = '3m', autoFetch = true) => {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!autoFetch) return; // Skip if lazy loading is disabled

    const fetchStocks = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (timeframe) params.append('timeframe', timeframe);
        const response = await fetch(`/api/stocks?${params.toString()}`);
        if (!response.ok) throw new Error('Failed to fetch stocks');

        const liveStocks = await response.json();

        // Generate all trajectories first
        const trajectories = liveStocks.map(stock => generateTrajectory(stock, 100));

        // Find global min/max prices across all stocks for Y-axis calibration
        let minPrice = Infinity;
        let maxPrice = -Infinity;
        trajectories.forEach(trajectory => {
          trajectory.forEach(point => {
            minPrice = Math.min(minPrice, point.price);
            maxPrice = Math.max(maxPrice, point.price);
          });
        });

        // Calculate scaling factor based on price range
        const priceRange = maxPrice - minPrice;
        const yScale = priceRange > 0 ? 8 / Math.log(maxPrice / minPrice) : 1; // Dynamic scaling

        // Enrich live data with trajectory for animation
        const enrichedStocks = liveStocks.map((stock, idx) => {
          // Recalculate trajectory with dynamic scaling
          const trajectory = trajectories[idx].map(point => ({
            ...point,
            // Recalculate Y with dynamic scaling
            y: Math.max(-10, Math.min(10, ((Math.log(point.price) - Math.log(minPrice)) / Math.log(maxPrice / minPrice) - 0.5) * 2 * yScale)),
          }));

          return {
            id: idx,
            symbol: stock.symbol,
            name: stock.name,
            color: getColorBySymbol(stock.symbol),
            industry: getIndustryBySymbol(stock.symbol),
            // Current position (from trajectory)
            x: trajectory[trajectory.length - 1].x,
            y: trajectory[trajectory.length - 1].y,
            z: trajectory[trajectory.length - 1].z,
            // Live data
            price: stock.price,
            priceChange: stock.changePercent,
            marketCap: 0, // Not provided by API
            volume: 0, // Not provided by API
            timestamp: new Date().toISOString(),
            // Time-series history
            trajectory: trajectory,
          };
        });

        setStocks(enrichedStocks);
        setError(null);
      } catch (err) {
        console.error('Error fetching stocks:', err);
        setError(err.message);
        // On error, still keep the component working with empty state
        setStocks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchStocks();
    // Poll for updates every 30 seconds
    const interval = setInterval(fetchStocks, 30000);

    return () => clearInterval(interval);
  }, [timeframe, autoFetch]);

  return { stocks, loading, error };
};

/**
 * Generate realistic price trajectory for time-based 2D visualization:
 * X-axis: Time progression (left to right as time advances)
 * Price data: Raw prices (Y-axis calculation done in hook with dynamic scaling)
 * Z-axis: Fixed at 0
 *
 * Arrow rotation: Represents momentum
 * - Fast spin = strong momentum
 * - Slow/no spin = stalling/weak momentum
 *
 * Visual metaphor: Like a financial chart you read left-to-right
 * Timeline progresses left → right, price goes up/down
 */
function generateTrajectory(stock, steps = 30) {
  const trajectory = [];

  // Seed random for realistic but repeatable movements per stock
  // Use stock symbol to seed so same stock gets same pattern each time
  const seed = stock.symbol.charCodeAt(0) + stock.symbol.charCodeAt(1);

  // Start with actual stock price - stay close to it throughout timeline
  let basePrice = stock.price;
  let volatility = 0.02;

  for (let t = 0; t < steps; t++) {
    // Small random walk around base price (±2% daily volatility)
    // This shows realistic price movement, not wild swings
    const randomFactor = Math.sin(t * 0.5 + seed) * 0.02 + Math.cos(t * 0.3 + seed) * 0.01;
    const dailyReturn = (randomFactor - 0.005) * (Math.random() - 0.5); // Small daily moves
    const price = basePrice * (1 + dailyReturn);
    const momentum = dailyReturn; // Current day's momentum

    // Volatility: slight variation but stable
    volatility = Math.max(0.01, Math.min(0.08, volatility + (Math.random() - 0.5) * 0.003));

    // Volume: realistic fluctuation
    const volume = 50000000 * (0.8 + Math.random() * 0.4);

    // Timeline mapping (time left-to-right):
    // X-axis: Time position (0 to 30 days)
    // Normalize to -8 to 8 range for consistent spacing
    const x = (t / (steps - 1)) * 16 - 8; // Maps 0→-8, steps-1→+8

    // Y-axis: Will be calculated in hook based on global min/max prices
    // Placeholder value (will be overwritten)
    const y = 0;

    // Z: Always 0 for clean 2D view
    const z = 0;

    trajectory.push({
      timestamp: new Date(Date.now() - (steps - t) * 86400000).toISOString(),
      x: x,
      y: y,
      z: z,
      price: price,
      priceChange: (dailyReturn * 100),
      momentum: momentum, // Current day momentum for rotation
      volatility: volatility,
      volume: volume,
    });

    basePrice = price;
  }

  return trajectory;
}

/**
 * Assign color based on stock sector
 */
function getColorBySymbol(symbol) {
  const techStocks = ['AAPL', 'MSFT', 'GOOGL', 'NVDA', 'META', 'TSLA'];
  const finStocks = ['JPM', 'BAC'];
  const healthStocks = ['JNJ'];

  if (techStocks.includes(symbol)) return '#00A8FF'; // Technology
  if (finStocks.includes(symbol)) return '#00D9FF'; // Finance
  if (healthStocks.includes(symbol)) return '#FF6B9D'; // Healthcare
  return '#00FF88'; // Consumer
}

/**
 * Assign industry based on stock
 */
function getIndustryBySymbol(symbol) {
  const sectors = {
    AAPL: 'Technology',
    MSFT: 'Technology',
    GOOGL: 'Technology',
    NVDA: 'Technology',
    META: 'Technology',
    TSLA: 'Consumer',
    AMZN: 'Consumer',
    JPM: 'Finance',
    JNJ: 'Healthcare',
  };

  return sectors[symbol] || 'Consumer';
}
